import { Button, ButtonMode, Modal, ModalWidth } from '@creditsights/styleguide-web';
import React, { useMemo, useState } from 'react';
import { SavedSearchDto } from '@creditsights/api-ui-web';
import classNames from 'classnames/bind';
import { useForm, useWatch } from 'react-hook-form';
import { useGlobalModalContext } from '../../GlobalModal';
import useModalErrorHandler from '../../../hooks/modal/useModalErrorHandler';

import styles from './SaveSearchModal.module.scss';
import { generateNextDefaultName } from '../../../utils/generateNextDefaultName';
import useCreateSavedSearch from '../../../hooks/searches/useCreateSavedSearch';
import { useToast } from '../../../hooks/toast/useToast';
import { GENERIC_ERROR_TEXT } from '../../../Base.constants';
import useUpdateSavedSearch from '../../../hooks/searches/useUpdateSavedSearch';
import { removeSubsequentSpaces } from '../../../utils/inputFormatUtils';
import useSelectedSavedSearch from '../../../hooks/searches/useSelectedSavedSearch';
import useFetchSavedSearches from '../../../hooks/searches/useFetchSavedSearches';
import { trackSaveSearch } from '../../../utils/trackUsageUtils';

const cx = classNames.bind(styles);

interface ConfirmSaveSearchModalProps {
  search: SavedSearchDto;
}

interface SaveSearchForm {
  name: string;
}

const SaveSearchModal = ({ search }: ConfirmSaveSearchModalProps): React.ReactElement => {
  const { closeModal, setError, store } = useGlobalModalContext();
  const { data, isFetching: isFetchingSearches } = useFetchSavedSearches();
  const { mutate: createSearch, isLoading: isCreatingSearch } = useCreateSavedSearch();
  const { mutate: updateSearch, isLoading: isUpdatingSearch } = useUpdateSavedSearch();
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToast();
  const [selectedSearchId, setSelectedSavedSearchId] = useSelectedSavedSearch();
  const savedSearches = data || [];
  const selectedSearchName = useMemo(() => {
    return savedSearches.find((search) => search.id === selectedSearchId)?.name;
  }, [savedSearches, selectedSearchId]);

  const isSavingAnything = isSaving || isCreatingSearch || isUpdatingSearch;
  const isModalCloseable = !isSavingAnything;

  useModalErrorHandler(setIsSaving);

  const generatedName = useMemo(
    () =>
      generateNextDefaultName(
        savedSearches.map((search) => search.name),
        'Default Search'
      ),
    [savedSearches]
  );

  const { register, handleSubmit, formState, control, setValue } = useForm<SaveSearchForm>({
    mode: 'onChange',
    defaultValues: {
      name: selectedSearchName || generatedName,
    },
  });
  const nameInputValue = useWatch({ control, name: 'name' });

  const trimmedName = useMemo(() => {
    return nameInputValue?.trim();
  }, [nameInputValue]);

  const editingSearchId = useMemo(() => {
    return savedSearches.find((search) => search.name === trimmedName)?.id;
  }, [savedSearches, trimmedName]);

  const { isValid, errors } = formState;

  const renderFooter = () => (
    <>
      <Button
        disabled={isSaving}
        dataTestId="save-search-cancel-button"
        onClick={closeModal}
        mode={ButtonMode.SECONDARY}
      >
        Cancel
      </Button>
      <Button
        disabled={!isValid || !trimmedName || isSavingAnything || isFetchingSearches}
        loading={isSavingAnything}
        onClick={handleSubmit(onSubmit)}
        dataTestId="save-search-confirm-button"
        mode={ButtonMode.PRIMARY}
      >
        {editingSearchId ? 'Update' : 'Save'}
      </Button>
    </>
  );

  const onSubmit = () => {
    if (!trimmedName) {
      return;
    }

    setError('');
    setIsSaving(true);
    onSave(trimmedName);
  };

  const onChange = (e) => {
    setValue('name', removeSubsequentSpaces(e.target.value));
  };

  const onSave = (name: string) => {
    if (editingSearchId) {
      updateSearch(
        {
          ...search,
          name,
          id: editingSearchId,
        },
        {
          onSuccess: ({ id }) => {
            closeModal();
            setSelectedSavedSearchId(id);
            showToast(
              <span>
                The search <strong>{name}</strong> has been updated
              </span>
            );
            trackSaveSearch(name);
          },
          onError: () => {
            setError(GENERIC_ERROR_TEXT);
          },
        }
      );
    } else {
      createSearch(
        {
          ...search,
          name,
        },
        {
          onSuccess: ({ id }) => {
            closeModal();
            setSelectedSavedSearchId(id);
            showToast(
              <span>
                This search has been saved as <strong>{name}</strong>
              </span>
            );
            trackSaveSearch(name);
          },
          onError: () => {
            setError(GENERIC_ERROR_TEXT);
          },
        }
      );
    }
  };

  return (
    <Modal
      width={ModalWidth.Medium}
      closeOnEscape={isModalCloseable}
      closeOnDimmerClick={isModalCloseable}
      open
      onClose={closeModal}
      headerTitle="Save Search"
      footer={renderFooter()}
      error={store.props.error}
      dataTestId="save-search-modal"
      className={cx('body')}
      closeOnButtonClick={isModalCloseable}
      isLoading={isFetchingSearches}
    >
      <form className={cx('form')} onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor="name">
          Saved Search Name
          <input
            id="name"
            className={cx('input', {
              'is-error': !isValid,
            })}
            data-testid="save-search-modal-search-name"
            type="text"
            placeholder="Provide a name for your saved search"
            {...register('name', {
              onChange,
              maxLength: { value: 100, message: 'The name of your saved search can’t have more than 100 characters' },
            })}
            autoFocus
          />
        </label>
        {!isValid && (
          <div className={cx('info-field', 'error')}>
            {Object.values(errors).map((e) => (
              <p key={e.message}>{e.message}</p>
            ))}
          </div>
        )}
        {editingSearchId && isValid && (
          <div className={cx('info-field')}>
            <p>A saved search with this name already exists. Continuing will update that saved search.</p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default SaveSearchModal;
